import type {
	CompactProxyOutput,
	CompactSenderGroupOutput,
} from "@/api/custom_types";
import { ReusableHotTable } from "@/components/CSV/ReusableHotTable";
import { SenderPriorityUsageEnum } from "@/constants";
import { useTranslation } from "@/hooks/useTranslation";
import { type Row, applyDisabledFlagsForRow } from "@/utils/csv-helpers";
import { type FC, memo, useMemo } from "react";
import { makeColumnSpecs } from "./helper";

export const AUTH_COLS = {
	backup: ["backupCode"] as const,
	gauth: ["gauth"] as const,
	imap: [
		"imapServer",
		"imapUsername",
		"imapPassword",
		"imapSsl",
		"imapPort",
	] as const,
} as const;

type CSVCols =
	| "email"
	| "password"
	| "proxyIp"
	| "senderGroupName"
	| "senderPriority"
	| "backupCode"
	| "gauth"
	| "imapServer"
	| "imapUsername"
	| "imapPassword"
	| "imapSsl"
	| "imapPort";

export type RowData = Row<CSVCols>;

export const columnsKey: (keyof RowData & string)[] = [
	"email",
	"password",
	"proxyIp",
	"senderGroupName",
	"senderPriority",
	"backupCode",
	"gauth",
	"imapServer",
	"imapUsername",
	"imapPassword",
	"imapSsl",
	"imapPort",
];

interface NewCSVSenderProps {
	data: RowData[];
	onChange: (rows: RowData[]) => void;
	proxies: CompactProxyOutput[];
	groups: CompactSenderGroupOutput[];
}

const NewCSVSender: FC<NewCSVSenderProps> = ({
	data,
	onChange,
	groups,
	proxies,
}) => {
	const { t } = useTranslation();

	const proxySource = useMemo(
		() =>
			proxies
				?.map((p) => {
					const clean = p.address.replace(/\/+$/, "");
					return `${clean}:${p.port}`;
				})
				.sort() ?? [],
		[proxies],
	);

	const groupSource = useMemo(
		() => groups?.map((g) => g.name).sort() ?? [],
		[groups],
	);

	const senderPriorityValues = [
		"INHERITED",
		...Object.values(SenderPriorityUsageEnum),
	];

	const columns = useMemo(
		() => makeColumnSpecs(t, proxySource, groupSource, senderPriorityValues),
		[t, proxySource, groupSource, senderPriorityValues],
	);

	return (
		<ReusableHotTable<RowData>
			data={data}
			columns={columns}
			hotProps={{
				minRows: 200,
				afterChange: function (this: any, changes, source) {
					if (
						changes &&
						(source === "edit" ||
							source === "CopyPaste.paste" ||
							source === "updateData" ||
							source === "Autofill.fill")
					) {
						for (const [row, prop] of changes) {
							if (
								columnsKey.includes(
									(prop as string).replace(".value", "") as CSVCols,
								)
							)
								applyDisabledFlagsForRow<RowData, typeof AUTH_COLS>(
									data[row],
									AUTH_COLS,
								);
						}

						const updated = this.getSourceData() as RowData[];
						onChange([...updated]);
					}
				},
			}}
		/>
	);
};

export default memo(NewCSVSender);
